import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  Autocomplete,
} from "@mui/material";
import axios from "axios";
import BaseURL from "../../api/AppURL";

export default function AddMultiAdmin({ onSaveSuccess }) {
  const [values, setValues] = useState({
    username: "",
    name: "",
    email: "",
    phone: "",
    address: "",
    password: "",
    roles: [], // multiple roles এর জন্য array হবে
  });

  const [roleOptions, setRoleOptions] = useState([]); // role objects: {id, name}
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const handleChange = (field) => (e) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
    setErrors((err) => ({ ...err, [field]: null }));
  };

  const validate = () => {
    const err = {};
    if (!values.username.trim()) err.username = "User Name is required";
    if (!values.name.trim()) err.name = "Full Name is required";
    if (!values.email.trim()) err.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(values.email)) err.email = "Invalid email";
    if (!values.password) err.password = "Password is required";

    // multiple roles এর জন্য পরিবর্তন:
    if (!values.roles || values.roles.length === 0) err.roles = "At least one role is required";

    setErrors(err);
    return Object.keys(err).length === 0;
  };



  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const res = await axios.get(`${BaseURL}/add/admin`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        if (res.data && res.data.roles) {
          setRoleOptions(res.data.roles); // পুরো role object রাখছি
        }
      } catch (err) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load roles from server",
        });
      }
    };
    fetchRoles();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Fix validation errors",
      });
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const postData = {
        username: values.username,
        name: values.name,
        email: values.email,
        phone: values.phone,
        address: values.address,
        password: values.password,
        roles: values.roles,    // role id পাঠানো হবে
      };
      const res = await axios.post(
        `${BaseURL}/admin/multirole/store`,
        postData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      setSnackbar({ open: true, severity: "success", message: res.data.message || "Admin created" });
      if (typeof onSaveSuccess === "function") onSaveSuccess(res.data.message);

      setValues({
        username: "",
        name: "",
        email: "",
        phone: "",
        address: "",
        password: "",
        roles: [],
      });
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to create admin";
      setSnackbar({ open: true, severity: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Add Admin
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            label="User Name"
            value={values.username}
            onChange={handleChange("username")}
            required
            error={!!errors.username}
            helperText={errors.username}
          />
          <TextField
            label="Full Name"
            value={values.name}
            onChange={handleChange("name")}
            required
            error={!!errors.name}
            helperText={errors.name}
          />
          <TextField
            label="Email"
            value={values.email}
            onChange={handleChange("email")}
            required
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            label="Phone"
            value={values.phone}
            onChange={handleChange("phone")}
          />
          <TextField
            label="Address"
            value={values.address}
            onChange={handleChange("address")}
            multiline
            rows={2}
          />
          <TextField
            label="Password"
            type="password"
            value={values.password}
            onChange={handleChange("password")}
            required
            error={!!errors.password}
            helperText={errors.password}
          />

          <Autocomplete
            multiple
            options={roleOptions}
            getOptionLabel={(option) => option.name}
            value={roleOptions.filter((r) => values.roles.includes(r.id))}
            onChange={(_, newValues) => {
              const roleIds = newValues.map((r) => r.id);
              setValues((v) => ({ ...v, roles: roleIds }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assign Roles"
                required
                error={!!errors.roles}
                helperText={errors.roles}
              />
            )}
          />


          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
